import { useEffect, useMemo, useState } from 'react'
import {
  ModalDialog,
  Mtds,
  widgets,
  Tasks,
  getSubstanceText,
  formatMatrixAmount,
  useGetOwnerNameQueries,
  templates,
  fields,
  type TaskDelegation
} from 'prove-common'
import {
  Form,
  type RJSFSchema,
  type IChangeEvent,
  customizeValidator
} from 'prove-common/rjsf'
import { useNavigate } from 'react-router'
import { isTaskCompleted, mergeMatrixAmounts } from '../../utils/taskHelper'

type DelegationByTaskId = Map<number, TaskDelegation>

const collectOwnerIds = (delegation?: TaskDelegation): string[] => {
  if (!delegation) return []
  const set = new Set<string>()
  delegation.entries.forEach((entry) => set.add(entry.ownerId))
  return Array.from(set)
}

const buildLeafOptions = (
  delegation: TaskDelegation | undefined,
  resolveName: (ownerId: string) => string
): { const: number; title: string }[] => {
  return (delegation?.entries ?? [])
    .filter((entry) => entry.isLeaf)
    .map((entry) => ({
      const: entry.taskDelegationId,
      title: resolveName(entry.ownerId)
    }))
}

const selectAnalysisSchema = (
  tasks: Tasks,
  delegations: DelegationByTaskId,
  selectedTaskId: number | undefined,
  resolveName: (ownerId: string) => string
): RJSFSchema => {
  const taskIdProperty: RJSFSchema = {
    type: 'number',
    title: 'Velg analyser for',
    description:
      'Denne oppgaven består av flere analyser. Du må derfor velge analysen som prøven skal registreres på. For analyser markert med (Ferdig) er alle planlagte prøver tatt.',
    oneOf: tasks?.map((task) => ({
      const: task.id,
      title:
        isTaskCompleted(task.totalCount, task.quantity) === true
          ? `${getSubstanceText(task.analysisType)} (Ferdig)`
          : getSubstanceText(task.analysisType)
    }))
  }

  const selectedTask = tasks?.find((t) => t.id === selectedTaskId)
  const subTaskLeafOptions = buildLeafOptions(
    selectedTask == null ? undefined : delegations.get(selectedTask.id),
    resolveName
  )

  const required: string[] = ['taskId']

  // Build properties object conditionally
  const properties: Record<string, RJSFSchema> = {
    taskId: taskIdProperty
  }

  if (selectedTaskId !== undefined && subTaskLeafOptions.length > 0) {
    properties.subTaskId = {
      type: 'number',
      title: 'Velg enhet',
      description:
        'Denne oppgaven er fordelt videre. Du må derfor velge hvilken enhet som prøven skal registreres på.',
      oneOf: subTaskLeafOptions
    }
    required.push('subTaskId')
  }

  return {
    type: 'object',
    properties,
    required
  }
}

const selectAnalysisUiSchema = {
  taskId: {
    'ui:widget': 'radio',
    'ui:stacked': true
  },
  subTaskId: {
    'ui:widget': 'radio',
    'ui:stacked': true
  }
}

type SelectAnalysisFormData = {
  taskId?: number
  subTaskId?: number
}

type RegisterSampleModalProps = {
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
  tasks: Tasks
  delegations: DelegationByTaskId
}

const RegisterSampleModal = ({
  isOpen,
  setOpen,
  tasks,
  delegations
}: RegisterSampleModalProps) => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState<SelectAnalysisFormData | undefined>(
    {}
  )

  // Selected task for memoized computations
  const selectedTask = useMemo(() => {
    return tasks?.find((t) => t.id === formData?.taskId)
  }, [tasks, formData?.taskId])

  const matrixAmount = useMemo(
    () =>
      mergeMatrixAmounts(
        (selectedTask == null ? (tasks ?? []) : [selectedTask]).map((t) => ({
          matrixAmount: formatMatrixAmount(t.matrixAmount)
        }))
      ),
    [tasks, selectedTask]
  )

  // Unique owner IDs under selected task
  const uniqueOwnerIds = useMemo(() => {
    return collectOwnerIds(
      selectedTask == null ? undefined : delegations.get(selectedTask.id)
    )
  }, [delegations, selectedTask])

  // Resolve owner names for IDs
  const { data: ownerNames } = useGetOwnerNameQueries(uniqueOwnerIds)

  const resolveOwnerName = useMemo(
    () => (ownerId: string) =>
      ownerNames.find((owner) => owner.id === ownerId)?.name ?? ownerId,
    [ownerNames]
  )

  // Initialize defaults when only one option exists
  useEffect(() => {
    if (!isOpen) return
    const singleTask = tasks?.length === 1 ? tasks[0] : undefined
    if (singleTask) {
      const leaves = buildLeafOptions(
        delegations.get(singleTask.id),
        resolveOwnerName
      )
      setFormData({
        taskId: singleTask.id,
        subTaskId: leaves.length === 1 ? leaves[0].const : undefined
      })
    }
  }, [delegations, isOpen, resolveOwnerName, tasks])

  const onSubmit = ({
    formData
  }: IChangeEvent<SelectAnalysisFormData>): void => {
    if (formData != null) {
      const taskId = formData?.subTaskId ?? formData.taskId
      const redirect = `/samples/:code/registration?taskId=${taskId}`
      navigate(`/scan?type=sample&redirectUrl=${encodeURIComponent(redirect)}`)
    }
  }

  const handleChange = (data: IChangeEvent<SelectAnalysisFormData>) => {
    const next = data.formData
    // If taskId changed, and the selected task has exactly one leaf subtask, set it by default.
    const prevTaskId = formData?.taskId
    const nextTaskId = next?.taskId

    if (nextTaskId !== prevTaskId) {
      const leaves = buildLeafOptions(
        nextTaskId == null ? undefined : delegations.get(nextTaskId),
        resolveOwnerName
      )

      setFormData({
        taskId: nextTaskId,
        // If there is exactly one leaf, preselect it; otherwise clear to force selection
        subTaskId: leaves.length === 1 ? leaves[0].const : undefined
      })
      return
    }

    // For other changes (like picking subTaskId), just accept the updated form data
    setFormData(next)
  }

  const validator = customizeValidator<SelectAnalysisFormData>()
  return (
    <ModalDialog
      id='register-sample-modal'
      closedby='any'
      open={isOpen}
      onClose={() => {
        setFormData({})
        setOpen(false)
      }}
    >
      <Mtds.Button
        command='close'
        commandfor='register-sample-modal'
        aria-label='Lukk'
      />
      <Mtds.Prose>
        <Mtds.Heading>Registrer prøve</Mtds.Heading>
        {matrixAmount != null && (
          <Mtds.Muted>Mengde prøvemateriale: {matrixAmount}</Mtds.Muted>
        )}
        <Form<SelectAnalysisFormData>
          noHtml5Validate
          schema={selectAnalysisSchema(
            tasks,
            delegations,
            formData?.taskId,
            resolveOwnerName
          )}
          uiSchema={selectAnalysisUiSchema}
          widgets={widgets}
          fields={fields}
          templates={templates}
          showErrorList={false}
          validator={validator}
          formData={formData}
          onChange={handleChange}
          onSubmit={onSubmit}
          experimental_defaultFormStateBehavior={{
            constAsDefaults: 'skipOneOf'
          }}
        >
          <Mtds.Flex style={{ paddingTop: 'var(--mtds-8)' }}>
            <Mtds.Button command='close' commandfor='register-sample-modal'>
              Avbryt
            </Mtds.Button>
            <Mtds.Button data-variant='primary' type='submit'>
              Gå videre
            </Mtds.Button>
          </Mtds.Flex>
        </Form>
      </Mtds.Prose>
    </ModalDialog>
  )
}
export default RegisterSampleModal
